import React, { useState } from 'react';
import { Plus, Loader2, Calendar, DollarSign, Trophy, Hash } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';

const contestSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  vote_price: z.coerce.number().min(0, 'Le prix doit être positif'),
  start_date: z.string().min(1, 'La date de début est requise'),
  end_date: z.string().min(1, 'La date de fin est requise'),
  status: z.enum(['draft', 'active', 'ended']),
  code_unique: z.string().min(1, 'Le code unique est requis'),
});

type ContestFormValues = z.infer<typeof contestSchema>;

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const generateUniqueCode = (): string => {
  // Générer un code aléatoire de 8 caractères (lettres majuscules et chiffres)
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const CreateContestButton = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContestFormValues>({
    resolver: zodResolver(contestSchema),
    defaultValues: {
      name: '',
      description: '',
      vote_price: 0,
      start_date: `${new Date().toISOString().split('T')[0]}T${new Date().toTimeString().slice(0, 5)}`,
      end_date: `${new Date().toISOString().split('T')[0]}T${new Date().toTimeString().slice(0, 5)}`,
      status: 'draft',
      code_unique: '',
    },
  });

  const onSubmit = async (data: ContestFormValues) => {
    try {
      setIsSubmitting(true);

      // Generate slug from name
      const slug = generateSlug(data.name);

      const { error } = await supabase
        .from('contests')
        .insert({
          name: data.name,
          description: data.description,
          vote_price: data.vote_price,
          start_date: new Date(data.start_date).toISOString(),
          end_date: new Date(data.end_date).toISOString(),
          status: data.status,
          slug: slug,
          code_unique: data.code_unique,
        });

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Le concours a été créé',
      });

      form.reset();
      setOpen(false);
      window.location.reload();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de créer le concours',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'text-yellow-400 bg-yellow-400/20';
      case 'active':
        return 'text-green-400 bg-green-400/20';
      case 'ended':
        return 'text-gray-400 bg-gray-400/20';
      default:
        return 'text-white bg-white/20';
    }
  };

  return (
    <>
      <button
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 transition-all duration-200 shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:ring-offset-2 focus:ring-offset-gray-900"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-5 w-5" />
        Nouveau Concours
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gray-900 border border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
              Créer un nouveau concours
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-6 pr-6 -mr-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Nom du concours</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Trophy className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <Input
                            {...field}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            placeholder="Miss Afrique 2025"
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 min-h-[100px]"
                          placeholder="Décrivez votre concours..."
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="vote_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Prix du vote</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <Input
                            type="number"
                            min="0"
                            {...field}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-gray-400">
                        Prix en FCFA pour un vote
                      </FormDescription>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-200">Date et heure de début</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            <Input
                              type="datetime-local"
                              {...field}
                              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-200">Date et heure de fin</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            <Input
                              type="datetime-local"
                              {...field}
                              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-400" />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Statut</FormLabel>
                      <FormControl>
                        <div className="grid grid-cols-3 gap-2">
                          {['draft', 'active', 'ended'].map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => field.onChange(status)}
                              className={`px-4 py-2 rounded-xl border border-white/10 transition-colors ${
                                field.value === status
                                  ? getStatusColor(status)
                                  : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              {status === 'draft' && 'Brouillon'}
                              {status === 'active' && 'Actif'}
                              {status === 'ended' && 'Terminé'}
                            </button>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="code_unique"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Code unique</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Hash className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <Input
                            {...field}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                            placeholder="CODE123"
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-gray-400">
                        Code unique pour identifier ce concours
                      </FormDescription>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="flex-shrink-0 pt-6 border-t border-white/10 mt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-white shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Création...
                    </div>
                  ) : (
                    'Créer'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
